import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AssetUpdaterService {
    private userAvatarUrlSubject = new BehaviorSubject<string | null>(null);
    private chevronImageUrlSubject = new BehaviorSubject<string | null>(null);
    private logoUrlSubject = new BehaviorSubject<string | null>(null);

    public userAvatarUrl$ = this.userAvatarUrlSubject.asObservable();
    public chevronImageUrl$ = this.chevronImageUrlSubject.asObservable();
    public logoUrl$ = this.logoUrlSubject.asObservable();

    constructor() {
        this.startCSSVariableMonitoring();
    }

    updateAssetsFromCSS(): void {
        const userAvatarUrl = this.getCSSVariableValue('--user-avatar-url');
        const chevronImageUrl = this.getCSSVariableValue('--chevron-image-url');
        const logoUrl = this.getCSSVariableValue('--logo-url');

        if (userAvatarUrl && this.isValidAssetUrl(userAvatarUrl)) {
            this.userAvatarUrlSubject.next(userAvatarUrl);
        }

        if (chevronImageUrl && this.isValidAssetUrl(chevronImageUrl)) {
            this.chevronImageUrlSubject.next(chevronImageUrl);
        }

        if (logoUrl && this.isValidAssetUrl(logoUrl)) {
            this.logoUrlSubject.next(logoUrl);
        }
    }

    private isValidAssetUrl(url: string): boolean {
        if (!url || typeof url !== 'string') {
            return false;
        }

        const trimmedUrl = url.trim();

        const invalidValues = [
            '', 'none', 'initial', 'inherit', 'unset', 'undefined', 'null',
            '[object Object]', '[object HTMLImageElement]', '[object', 'object'
        ];

        if (invalidValues.some(invalid => trimmedUrl.includes(invalid))) {
            return false;
        }

        return trimmedUrl.length > 3 && trimmedUrl.includes('.');
    }

    private getCSSVariableValue(cssVariable: string): string | null {
        const value = getComputedStyle(document.documentElement)
            .getPropertyValue(cssVariable)?.trim();

        if (!value || value === '' || value === 'none') {
            return null;
        }

        return value.replace(/^url\(['"]?|['"]?\)$/g, '');
    }

    private startCSSVariableMonitoring(): void {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    this.updateAssetsFromCSS();
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style']
        });
    }

    forceUpdate(): void {
        this.updateAssetsFromCSS();
    }

    getCurrentUserAvatarUrl(): string | null {
        return this.userAvatarUrlSubject.value;
    }

    getCurrentChevronImageUrl(): string | null {
        return this.chevronImageUrlSubject.value;
    }

    getCurrentLogoUrl(): string | null {
        return this.logoUrlSubject.value;
    }

    resetAssets(): void {
        this.userAvatarUrlSubject.next(null);
        this.chevronImageUrlSubject.next(null);
        this.logoUrlSubject.next(null);
    }

    /**
     * Actualiza un asset específico solo si es válido
     */
    updateUserAvatarUrl(url: string | null): void {
        if (url && this.isValidAssetUrl(url)) {
            this.userAvatarUrlSubject.next(url);
        }
    }

    updateChevronImageUrl(url: string | null): void {
        if (url && this.isValidAssetUrl(url)) {
            this.chevronImageUrlSubject.next(url);
        }
    }

    updateTopbarLogoUrl(url: string | null): void {
        if (url && this.isValidAssetUrl(url)) {
            this.logoUrlSubject.next(url);
        }
    }
}
